// Define types for the utility functions
export interface CountryCodeMap {
  [key: string]: string;
}

export interface RegionMap {
  [key: string]: string;
}

export interface ExportRowOptions {
  region: string;
  country: string;
  kpi: string;
  year: number;
  startDate: string;
  endDate: string;
  value: number;
  unit: string;
  businessSegment?: string;
}

export interface IntensityRowOptions {
  region: string;
  country: string;
  renewableIntensity: number;
  nonRenewableIntensity: number;
  residualGridIntensity: number;
  residualSuffix: string;
  year: number;
  startDate: string;
  endDate: string;
  businessSegment?: string;
}

export interface VolumeRowOptions {
  region: string;
  country: string;
  renewableVolume: number;
  nonRenewableVolume: number;
  residualGridVolume: number;
  residualSuffix: string;
  year: number;
  startDate: string;
  endDate: string;
  businessSegment?: string;
}

export interface CountryVolumeRegion {
  'grid-region': string;
  R4Q: number;
  'renewable-volume': number;
  'non-renewable': number;
  'residual-grid': number;
}

export interface CountryVolume {
  totalRenewable: number;
  totalNonRenewable: number;
  totalResidualGrid: number;
  totalR4Q: number;
  regions: CountryVolumeRegion[];
}

export interface ExportableData {
  totalVolumes: Record<string, CountryVolume>;
  renewableIntensities?: Record<string, { renewableIntensity: number }>;
  nonRenewableIntensities?: Record<string, { nonRenewableIntensity: number }>;
  residualIntensities?: Record<string, { residualGridIntensity: number }>;
}

/**
 * Map country name to country code.
 * @param country - Full country name.
 * @param countryCodeMap - Map of country names to codes.
 * @returns Country code or original country name.
 */
export function mapCountryToCode(country: string, countryCodeMap: CountryCodeMap): string {
  return countryCodeMap[country] || country;
}

/**
 * Map country code to region name.
 * @param countryCode - Country code.
 * @param regionMap - Map of country codes to region names.
 * @returns Region name or "Other".
 */
export function mapCountryCodeToRegion(countryCode: string, regionMap: RegionMap): string {
  return regionMap[countryCode] || "Other";
}

/**
 * Get special suffix for a country.
 * @param countryCode - Country code.
 * @returns Suffix string or empty string.
 */
export function getCountrySpecialSuffix(countryCode: string): string {
  const suffixMap: Record<string, string> = {
    US: " (Shell Energy North America (US), L.P. plus TEJAS POWER GENERATION LLC)",
  };
  return suffixMap[countryCode] || "";
}

/**
 * Get KPI label based on business segment type.
 * @param baseKPI - Basic KPI label.
 * @param businessSegment - Business segment code (SE or SET).
 * @returns Formatted KPI label.
 */
export function getKPILabel(baseKPI: string, businessSegment = "SET"): string {
  const kpiMap: Record<string, string> = {
    "Residual Volume": "Residual Volume",
    "Non Renewables PPA": "Non Renewable Volume",
    "REC Retirement plus REC Transfer Volume": "Renewable Volume",
    "CI_Residual Volume": "CI_Residual Volume",
    "CI_Non Renewables PPA": "CI_Non Renewable Volume",
    "CI_REC Retirement plus REC Transfer Volume": "CI_Renewable Volume",
  };

  if(businessSegment) {
    return kpiMap[baseKPI] || baseKPI;
  }
  return baseKPI;
}

/**
 * Create a data row for export.
 * @param options - Export row options.
 * @returns Data row.
 */
export function createExportRow(options: ExportRowOptions): Record<string, string | number> {
  const { region, country, kpi, year, startDate, endDate, value, unit, businessSegment = "SE" } = options;

  let adjustedUnit

  if(unit === 'gCO2e/KWH') {
    adjustedUnit = 'gCO2e/kWh';
  } else {
    adjustedUnit = adjustUnit(unit, businessSegment);

  }

  return {
    Region: region,
    Country: country,
    KPI: kpi,
    "Delivery Year": year,
    "Delivery Period Start": startDate,
    "Delivery Period End": endDate,
    Value: value.toFixed(2),
    "Unit of Measure": adjustedUnit,
  };
}

/**
 * Adjust the unit based on the business segment.
 * @param unit - Original unit.
 * @param businessSegment - Business segment (SE or SET).
 * @returns Adjusted unit.
 */
function adjustUnit(unit: string, businessSegment: string): string {
  const energyUnits = ["MWH", "TWH", "KWH"];
  if (energyUnits.some((u) => unit.toUpperCase().includes(u))) {
    return businessSegment === "SET" ? "MWH" : "TWH";
  }
  return unit;
}

/**
 * Create intensity data rows for export.
 * @param options - Intensity rows options.
 * @returns Array of intensity data rows.
 */
export function createIntensityRows(options: IntensityRowOptions): Record<string, string | number>[] {
  const { region, country, renewableIntensity, nonRenewableIntensity, residualGridIntensity, residualSuffix, year, startDate, endDate, businessSegment = "SE" } = options;

  return [
    createExportRow({
      region,
      country,
      kpi: getKPILabel("CI_REC Retirement plus REC Transfer Volume", businessSegment),
      year,
      startDate,
      endDate,
      value: renewableIntensity,
      unit: "gCO2e/KWH",
      businessSegment,
    }),
    createExportRow({
      region,
      country,
      kpi: getKPILabel("CI_Non Renewables PPA", businessSegment),
      year,
      startDate,
      endDate,
      value: nonRenewableIntensity,
      unit: "gCO2e/KWH",
      businessSegment,
    }),
    createExportRow({
      region,
      country,
      kpi: getKPILabel(`CI_Residual Volume${residualSuffix}`, businessSegment),
      year,
      startDate,
      endDate,
      value: residualGridIntensity,
      unit: "gCO2e/KWH",
      businessSegment,
    }),
  ];
}

/**
 * Create volume data rows for export.
 * @param options - Volume rows options.
 * @returns Array of volume data rows.
 */
export function createVolumeRows(options: VolumeRowOptions): Record<string, string | number>[] {
  const { region, country, renewableVolume, nonRenewableVolume, residualGridVolume, residualSuffix, year, startDate, endDate, businessSegment = "SE" } = options;
  const energyUnit = businessSegment === "SET" ? "MWH" : "TWH";

  return [
    createExportRow({
      region,
      country,
      kpi: getKPILabel("REC Retirement plus REC Transfer Volume", businessSegment),
      year,
      startDate,
      endDate,
      value: renewableVolume,
      unit: energyUnit,
      businessSegment,
    }),
    createExportRow({
      region,
      country,
      kpi: getKPILabel("Non Renewables PPA", businessSegment),
      year,
      startDate,
      endDate,
      value: nonRenewableVolume,
      unit: energyUnit,
      businessSegment,
    }),
    createExportRow({
      region,
      country,
      kpi: getKPILabel(`Residual Volume${residualSuffix}`, businessSegment),
      year,
      startDate,
      endDate,
      value: residualGridVolume,
      unit: energyUnit,
      businessSegment,
    }),
  ];
}

/**
 * Create exportable data for Excel.
 * @param data - The calculation results.
 * @param businessSegment - Business segment (SE or SET).
 * @param year - The year for delivery.
 * @param quarter - The quarter (Q1, Q2, Q3, Q4).
 * @returns Formatted data ready for Excel export.
 */
export function createExportableData(data: ExportableData, businessSegment = "SE", year = new Date().getFullYear(), quarter = "Q1"): { combinedData: Record<string, string | number>[] } {
  const { startDate, endDate } = getQuarterDateRange(year, quarter);
  const regionMap = getRegionMap();

  const intensityData: Record<string, string | number>[] = [];
  const volumeData: Record<string, string | number>[] = [];

  Object.keys(data.totalVolumes || {}).forEach((country) => {
    const region = regionMap[country] || "Other";
    const residualSuffix = getCountrySpecialSuffix(country);

    intensityData.push(
      ...createIntensityRows({
        region,
        country,
        renewableIntensity: data.renewableIntensities?.[country]?.renewableIntensity ?? 0,
        nonRenewableIntensity: data.nonRenewableIntensities?.[country]?.nonRenewableIntensity ?? 0,
        residualGridIntensity: data.residualIntensities?.[country]?.residualGridIntensity ?? 0,
        residualSuffix,
        year,
        startDate,
        endDate,
        businessSegment,
      })
    );

    volumeData.push(
      ...createVolumeRows({
        region,
        country,
        renewableVolume: data.totalVolumes?.[country]?.totalRenewable ?? 0,
        nonRenewableVolume: data.totalVolumes?.[country]?.totalNonRenewable ?? 0,
        residualGridVolume: data.totalVolumes?.[country]?.totalResidualGrid ?? 0,
        residualSuffix,
        year,
        startDate,
        endDate,
        businessSegment,
      })
    );
  });

  return {
    combinedData: [...intensityData, ...volumeData],
  };
}

/**
 * Get the date range for a quarter.
 * @param year - The year.
 * @param quarter - The quarter (Q1, Q2, Q3, Q4).
 * @returns Start and end dates for the quarter.
 */
function getQuarterDateRange(year: number, quarter: string): { startDate: string; endDate: string } {
  const quarterStartMap: Record<string, string> = {
    Q1: "01/01",
    Q2: "04/01",
    Q3: "07/01",
    Q4: "10/01",
  };

  const quarterEndMap: Record<string, string> = {
    Q1: "03/31",
    Q2: "06/30",
    Q3: "09/30",
    Q4: "12/31",
  };

  return {
    startDate: `${quarterStartMap[quarter] || "01/01"}/${year}`,
    endDate: `${quarterEndMap[quarter] || "03/31"}/${year}`,
  };
}

/**
 * Get the region map.
 * @returns Map of country names to regions.
 */
function getRegionMap(): RegionMap {
  return {
    "United States": "Americas",
    Canada: "Americas",
    Mexico: "Americas",
    Brazil: "Americas",
    "United Kingdom": "Europe",
    Germany: "Europe",
    Italy: "Europe",
    Spain: "Europe",
    Netherlands: "Europe",
    Australia: "Australia",
    India: "Asia",
    Singapore: "Asia",
    Nigeria: "Africa",
  };
}